import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Text, Html } from '@react-three/drei';
import * as THREE from 'three';

const Photons = () => {
  const particlesRef = useRef<THREE.Points>(null);
  
  const count = 150;
  const initialPositions = useMemo(() => {
    const pos = new Float32Array(count * 3);
    for(let i=0; i<count; i++) {
      pos[i*3] = -10 - Math.random() * 10; // x starts far left
      pos[i*3+1] = (Math.random() - 0.5) * 6; // y spread
      pos[i*3+2] = (Math.random() - 0.5) * 6; // z spread
    }
    return pos;
  }, []);

  const particlesGeo = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(initialPositions), 3));
    return geo;
  }, [initialPositions]);

  useFrame((state, delta) => {
    if (!particlesRef.current) return;
    const positions = particlesRef.current.geometry.attributes.position.array as Float32Array;
    
    for(let i=0; i<count; i++) {
      let x = positions[i*3];
      let y = positions[i*3+1];
      let z = positions[i*3+2];
      
      const speed = 4 * delta;
      x += speed; // move right
      
      // Aperture is at x = -2, with radius = 2
      const distFromCenter = Math.sqrt(y*y + z*z);
      
      if (x > -2 && distFromCenter < 1.9) {
        // Inside tube, converge towards focal point at x = 3, y=0, z=0
        // We gently lerp y and z towards 0 as x goes from -2 to 3
        const progress = Math.max(0, Math.min(1, (x + 2) / 5));
        
        // Target y and z are 0
        y -= y * progress * 0.1;
        z -= z * progress * 0.1;
      }
      
      // Reset if passed focal point or off screen
      if (x > 5 || (x > -2 && distFromCenter >= 1.9 && x > 2)) {
        x = -10 - Math.random() * 5;
        y = (Math.random() - 0.5) * 6;
        z = (Math.random() - 0.5) * 6;
      }
      
      positions[i*3] = x;
      positions[i*3+1] = y;
      positions[i*3+2] = z;
    }
    particlesRef.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <points ref={particlesRef} geometry={particlesGeo}>
      <pointsMaterial color="#FDE047" size={0.15} transparent opacity={0.8} />
    </points>
  );
};

const FocalPoint = () => {
  const meshRef = useRef<THREE.Mesh>(null);
  useFrame((state) => {
    if (meshRef.current) {
      const scale = 1 + Math.sin(state.clock.elapsedTime * 5) * 0.3;
      meshRef.current.scale.set(scale, scale, scale);
    }
  });

  return (
    <mesh ref={meshRef} position={[3, 0, 0]}>
      <sphereGeometry args={[0.2, 16, 16]} />
      <meshBasicMaterial color="#FDE047" transparent opacity={0.8} />
      <Html center position={[0.5, -0.5, 0]}>
         <div style={{ color: '#E2E8F0', fontSize: '20px' }}>👁️</div>
      </Html>
    </mesh>
  );
};

export const TelescopePhotons: React.FC<{ width?: number | string; height?: number | string }> = ({ width = '100%', height = 400 }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: '100%', height: Number(height) - 40, borderRadius: '8px', overflow: 'hidden', border: '1px solid #1E293B' }}>
        <Canvas camera={{ position: [0, 4, 10], fov: 45 }}>
          <ambientLight intensity={0.4} />
          <directionalLight position={[5, 5, 5]} intensity={1} />
          
          {/* Telescope Tube */}
          <mesh position={[0.5, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[2, 2, 5, 32, 1, true]} />
            <meshStandardMaterial color="#334155" side={THREE.DoubleSide} metalness={0.5} roughness={0.5} transparent opacity={0.6} />
          </mesh>
          
          {/* Lens */}
          <mesh position={[-2, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[1.95, 1.95, 0.2, 32]} />
            <meshStandardMaterial color="#88CCFF" transparent opacity={0.3} glass />
          </mesh>

          <Photons />
          <FocalPoint />

          <OrbitControls enableZoom={true} enablePan={false} />
        </Canvas>
      </div>
      <div style={{ marginTop: '12px', fontSize: '14px', color: '#94A3B8' }}>
        Wide aperture = more light gathered
      </div>
    </div>
  );
};
